/**
 * FEMesh.ts
 * -----------------------------------------------------------------------------
 * Renders the finite-element analysis mesh (the model sent to the SAP solver):
 * shells as translucent surfaces with their element-edge grid, frames + links as
 * line segments, joints as points. Built from the JSON emitted by the C#
 * FEMeshExporter — coordinates are already in viewer space (metres, Y-up), so no
 * conversion here.
 *
 * Each element type is a separate object parented under `root`, kept in `byType`
 * so the Explorer's Elements checkboxes can toggle visibility independently.
 */

import * as THREE from 'three';
import type { Viewport3D } from './Viewport3D';

export interface FEPayload {
  units?: string;
  joints?: number[];      // point coords [x,y,z …]
  frames?: number[];      // segment endpoint pairs
  links?: number[];       // segment endpoint pairs
  shellTris?: number[];   // triangles [x,y,z ×3 …]
  shellEdges?: number[];  // element-outline segment pairs
  solidTris?: number[];   // triangles
  restraints?: number[];  // restrained joint coords
  frameTris?: number[];   // extruded frame solids (box/cylinder prisms) — the "Extrude" toggle
  shellExtrudeTris?: number[]; // extruded shell solids (± half-thickness along the normal) — the "Extrude" toggle
  shellTriElem?: number[]; // per shell triangle → shell element index (picking)
  frameTriElem?: number[]; // per frameTris triangle → frame index (picking, extruded mode)
  shellExtrudeTriElem?: number[]; // per shellExtrudeTris triangle → shell index (picking, extruded mode)
  jointColors?: number[];      // per-joint RGB (0..1), parallel to `joints` — contour coloring
  frameColors?: number[];      // per-vertex RGB, parallel to `frames`
  frameTriColors?: number[];   // per-vertex RGB, parallel to `frameTris`
  shellTriColors?: number[];   // per-vertex RGB, parallel to `shellTris`
  shellExtrudeTriColors?: number[]; // per-vertex RGB, parallel to `shellExtrudeTris`
  diagramTris?: number[];      // Results ▸ Forces frame diagram ribbon triangles (un-extruded view)
  diagramTriColors?: number[]; // per-vertex RGB, parallel to `diagramTris`
  diagramTriElem?: number[];   // per diagramTris triangle → frame index (picking)
  diagramLines?: number[];     // frame diagram outline segment pairs (3D toolbar ▸ Settings ▸ Diagram outline)
}

export class FEScene {
  public readonly root = new THREE.Group();

  private readonly disposables: Array<THREE.BufferGeometry | THREE.Material> = [];
  /** Element type (matches the tree's checkbox labels) → its renderable. */
  private readonly byType = new Map<string, THREE.Object3D>();
  /** Bright overlay for the Explorer-selected component(s). */
  private highlight: THREE.Group | null = null;
  private readonly hlDisposables: Array<THREE.BufferGeometry | THREE.Material> = [];

  /** Pickable renderables + maps (canvas selection of frame/shell elements). */
  public shellMesh: THREE.Mesh | null = null;
  public shellExtrudeMesh: THREE.Mesh | null = null;
  public frameLines: THREE.LineSegments | null = null;
  public frameMesh: THREE.Mesh | null = null;
  public shellTriElem: number[] = [];
  public shellExtrudeTriElem: number[] = [];
  public frameTriElem: number[] = [];
  /** Results ▸ Forces frame diagram ribbon (un-extruded view) — a distinct
   *  pickable mesh alongside `frameLines`, not a replacement for it. */
  public diagramMesh: THREE.Mesh | null = null;
  public diagramTriElem: number[] = [];
  /** The outline round the diagram (Settings ▸ Diagram outline), drawn over `diagramMesh`. */
  private diagramLines: THREE.LineSegments | null = null;
  /** "Extrude" ribbon toggle — persists across setMesh calls (mode/step changes
   *  re-render the mesh, but shouldn't silently drop the user's display choice). */
  private extrudeOn = false;
  /** Settings ▸ Shell transparency: the shell surfaces' opacity, flat and extruded alike.
   *  Null keeps the defaults (flat 0.55, extruded opaque). Held across setMesh, like extrudeOn. */
  private shellOpacity: number | null = null;

  // Tracked so updateFrame() can push new positions/colors into their existing
  // buffers during animation playback without rebuilding the whole scene.
  private jointsObj: THREE.Points | null = null;
  private shellEdgesObj: THREE.LineSegments | null = null;
  private linksObj: THREE.LineSegments | null = null;
  private restraintsObj: THREE.Points | null = null;

  constructor(viewport: Viewport3D) {
    this.root.name = 'AnalysisMesh';
    this.root.visible = false;
    viewport.scene.add(this.root);
  }

  public get hasContent(): boolean {
    return this.root.children.length > 0;
  }

  public setMesh(payload: FEPayload): void {
    this.clear();
    const p = payload ?? {};

    // Shells: translucent flat surface + element-edge grid (default), or an extruded
    // solid slab (± half-thickness along the normal — the "Extrude" toggle, shared
    // with Frames), grouped so one Elements checkbox controls whichever is active.
    const shellGroup = new THREE.Group();
    shellGroup.name = 'Shells';
    const shellMesh = this.buildSurface(p.shellTris, 0x8d8df0, this.shellOpacity ?? 0.55, undefined, p.shellTriColors);
    if (shellMesh) { shellMesh.visible = !this.extrudeOn; shellGroup.add(shellMesh); }
    const shellEdges = this.buildLines(p.shellEdges, 0x3535a5, 1);
    if (shellEdges) { shellEdges.visible = !this.extrudeOn; shellGroup.add(shellEdges); }
    const shellExtrudeMesh = this.buildSurface(p.shellExtrudeTris, 0x8d8df0, this.shellOpacity ?? 1, undefined, p.shellExtrudeTriColors);
    if (shellExtrudeMesh) { shellExtrudeMesh.visible = this.extrudeOn; shellGroup.add(shellExtrudeMesh); }
    if (shellGroup.children.length) this.add('Shells', shellGroup);
    this.shellMesh = shellMesh;
    this.shellExtrudeMesh = shellExtrudeMesh;
    this.shellTriElem = p.shellTriElem ?? [];
    this.shellExtrudeTriElem = p.shellExtrudeTriElem ?? [];

    const solids = this.buildSurface(p.solidTris, 0x9a9ad8, 0.6);
    if (solids) this.add('Solids', solids);

    // Frames: thin lines (default) or an extruded solid prism (the "Extrude" toggle),
    // grouped like Shells so one Elements checkbox controls whichever is active.
    const frameGroup = new THREE.Group();
    frameGroup.name = 'Frames';
    const frameLines = this.buildLines(p.frames, 0x1f8a1f, 1.5, undefined, p.frameColors);
    if (frameLines) { frameLines.visible = !this.extrudeOn; frameGroup.add(frameLines); }
    const frameMesh = this.buildSurface(p.frameTris, 0x2f6e2f, 1, undefined, p.frameTriColors);
    if (frameMesh) { frameMesh.visible = this.extrudeOn; frameGroup.add(frameMesh); }
    // Results ▸ Forces diagram ribbon: shown alongside the thin baseline line
    // (un-extruded view only) — a double-sided flat surface so it reads from
    // either side, colored per station like the shell/frame contours.
    const diagramMesh = this.buildDiagram(p.diagramTris, p.diagramTriColors);
    if (diagramMesh) { diagramMesh.visible = !this.extrudeOn; frameGroup.add(diagramMesh); }
    const diagramLines = this.buildLines(p.diagramLines, 0x4a4a4a, 1.5);
    if (diagramLines) { diagramLines.visible = !this.extrudeOn; frameGroup.add(diagramLines); }
    this.diagramLines = diagramLines;
    if (frameGroup.children.length) this.add('Frames', frameGroup);
    this.frameLines = frameLines;
    this.frameMesh = frameMesh;
    this.frameTriElem = p.frameTriElem ?? [];
    this.diagramMesh = diagramMesh;
    this.diagramTriElem = p.diagramTriElem ?? [];

    const links = this.buildLines(p.links, 0xc23bd6, 1.5);
    if (links) this.add('Links', links);
    this.linksObj = links;

    const joints = this.buildPoints(p.joints, 0xff2a2a, 3.2, undefined, p.jointColors);
    if (joints) this.add('Joints', joints);
    this.jointsObj = joints;
    this.shellEdgesObj = shellEdges;

    const restraints = this.buildPoints(p.restraints, 0x12b886, 7.5);
    if (restraints) this.add('Restraints', restraints);
    this.restraintsObj = restraints;
  }

  /**
   * Element Results "Play": push one already-computed animation frame straight
   * into the EXISTING geometry buffers (`BufferAttribute.array.set(...)` +
   * `needsUpdate`) instead of `setMesh()`'s disposeAndRebuild — keeps GPU memory
   * flat no matter how many frames play, and skips per-frame
   * `computeVertexNormals()` (shading is corrected by the full setMesh() the
   * caller does once animation stops). `FEAnimator` computes `px/py/pz` (dense,
   * one entry per joint AnalysisIndex, raw mm/model axes) and gathers them
   * through each representation's own joint-id mapping — this only writes.
   */
  public applyAnimationFrame(
    ids: {
      jointIds: number[]; restraintIds: number[]; frameIds: number[];
      linkIds: number[]; shellTriIds: number[]; shellEdgeIds: number[];
    },
    px: Float64Array, py: Float64Array, pz: Float64Array,
    colorField?: { r?: number[]; g?: number[]; b?: number[] },
  ): void {
    this.writeGather(this.jointsObj, ids.jointIds, px, py, pz, colorField);
    this.writeGather(this.shellEdgesObj, ids.shellEdgeIds, px, py, pz, undefined);
    this.writeGather(this.linksObj, ids.linkIds, px, py, pz, undefined);
    this.writeGather(this.restraintsObj, ids.restraintIds, px, py, pz, undefined);
    this.writeGather(this.frameLines, ids.frameIds, px, py, pz, colorField);
    this.writeGather(this.shellMesh, ids.shellTriIds, px, py, pz, colorField);
  }

  /** One representation's vertices are each "joint id[i]" — gather that joint's
   *  deformed position (mm → m, Z-up → Y-up, matching FEMeshExporter.AddVertexXYZ)
   *  and, when given, its color, straight into the existing buffer. No-ops if
   *  `obj` isn't part of the current mesh, or the vertex count doesn't match
   *  (should only happen if the mesh changed without a fresh loadFEAnimationMesh). */
  private writeGather(
    obj: THREE.Points | THREE.LineSegments | THREE.Mesh | null,
    ids: number[],
    px: Float64Array, py: Float64Array, pz: Float64Array,
    colorField?: { r?: number[]; g?: number[]; b?: number[] },
  ): void {
    if (!obj || !ids.length) return;
    const geom = obj.geometry as THREE.BufferGeometry;
    const posAttr = geom.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (posAttr && posAttr.count === ids.length) {
      const arr = posAttr.array as Float32Array;
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        arr[i * 3] = px[id] * 0.001;
        arr[i * 3 + 1] = pz[id] * 0.001;
        arr[i * 3 + 2] = -py[id] * 0.001;
      }
      posAttr.needsUpdate = true;
    }
    if (colorField?.r && colorField.g && colorField.b) {
      const colorAttr = geom.getAttribute('color') as THREE.BufferAttribute | undefined;
      if (colorAttr && colorAttr.count === ids.length) {
        const arr = colorAttr.array as Float32Array;
        const { r, g, b } = colorField;
        for (let i = 0; i < ids.length; i++) {
          const id = ids[i];
          arr[i * 3] = r[id] ?? 0.53;
          arr[i * 3 + 1] = g[id] ?? 0.53;
          arr[i * 3 + 2] = b[id] ?? 1;
        }
        colorAttr.needsUpdate = true;
      }
    }
  }

  /** Extruded frames ("Extrude" toggle): FEAnimator rebuilds the full prism
   *  triangle soup each frame (fixed vertex count — see FEAnimator's buffer
   *  precompute), so this only needs a straight `.set()` into the existing
   *  buffer, length-guarded the same way `writeGather` is. */
  public applyFrameExtrude(positions: Float32Array, colors: Float32Array | null): void {
    this.writeDense(this.frameMesh, positions, colors);
  }

  /** Extruded shells ("Extrude" toggle) — see {@link applyFrameExtrude}. */
  public applyShellExtrude(positions: Float32Array, colors: Float32Array | null): void {
    this.writeDense(this.shellExtrudeMesh, positions, colors);
  }

  private writeDense(obj: THREE.Mesh | null, positions: Float32Array, colors: Float32Array | null): void {
    if (!obj) return;
    const geom = obj.geometry as THREE.BufferGeometry;
    const posAttr = geom.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (posAttr && posAttr.array.length === positions.length) {
      (posAttr.array as Float32Array).set(positions);
      posAttr.needsUpdate = true;
    } else {
      // Lengths can legitimately drift from the static mesh this geometry was built
      // with (e.g. the static extruded mesh was last rebuilt with a different frame/
      // shell count) — rebuild the attribute at FEAnimator's size instead of silently
      // dropping every future frame's update (the previous behavior: the extruded
      // solid would freeze at its last static pose for the rest of the Play session).
      geom.setAttribute('position', new THREE.BufferAttribute(positions.slice(), 3));
    }
    if (colors) {
      const colorAttr = geom.getAttribute('color') as THREE.BufferAttribute | undefined;
      if (colorAttr && colorAttr.array.length === colors.length) {
        (colorAttr.array as Float32Array).set(colors);
        colorAttr.needsUpdate = true;
      } else {
        geom.setAttribute('color', new THREE.BufferAttribute(colors.slice(), 3));
      }
    }
  }

  /** Toggle one element type's visibility (Joints/Frames/Shells/Solids/Links/Restraints). */
  public setVisible(type: string, on: boolean): void {
    if (on) this.hiddenTypes.delete(type); else this.hiddenTypes.add(type);
    const o = this.byType.get(type);
    if (o) o.visible = on;
  }

  /** Element types the host has hidden. Kept across setMesh, as the host keeps them: a
   *  results redraw (another case, force or diagram style) must not bring back what the
   *  Elements dropdown or Show Selected hid while the dropdown still says it is hidden. */
  private readonly hiddenTypes = new Set<string>();

  /** "Extrude" ribbon toggle: swap Frames between thin lines and the extruded solid
   *  prism, and Shells between the flat surface and the extruded slab. Persisted so
   *  the next setMesh (a mode/step change) keeps the choice. */
  /** Settings ▸ Shell transparency: restyle the shell surfaces in place — no rebuild. */
  public setShellOpacity(opacity: number): void {
    const o = Math.max(0.02, Math.min(1, opacity));
    this.shellOpacity = o;
    for (const mesh of [this.shellMesh, this.shellExtrudeMesh]) {
      const m = mesh?.material as THREE.MeshStandardMaterial | undefined;
      if (!m) continue;
      m.opacity = o;
      // Opaque again at 100%: a transparent material sorts and blends for nothing.
      const transparent = o < 1;
      if (m.transparent !== transparent) { m.transparent = transparent; m.needsUpdate = true; }
    }
  }

  public setExtrude(on: boolean): void {
    this.extrudeOn = on;
    if (this.frameLines) this.frameLines.visible = !on;
    if (this.frameMesh) this.frameMesh.visible = on;
    if (this.diagramMesh) this.diagramMesh.visible = !on;
    if (this.diagramLines) this.diagramLines.visible = !on;
    if (this.shellMesh) this.shellMesh.visible = !on;
    if (this.shellExtrudeMesh) this.shellExtrudeMesh.visible = on;
    // The flat-plane edge grid sits mid-thickness inside the extruded slab — hide it
    // there so it doesn't read as a seam through the solid.
    const shellGroup = this.byType.get('Shells');
    for (const child of shellGroup?.children ?? []) {
      if (child instanceof THREE.LineSegments) child.visible = !on;
    }
  }

  /** Disable depth testing on every element type so the mesh renders on top of the
   *  bridge geometry regardless of camera angle (the ribbon's "Disable DepthTest"). */
  public setDepthTest(disabled: boolean): void {
    for (const obj of this.byType.values()) {
      const mat = (obj as THREE.Mesh | THREE.LineSegments | THREE.Points).material as
        THREE.Material | THREE.Material[] | undefined;
      if (!mat) continue;
      for (const m of Array.isArray(mat) ? mat : [mat]) m.depthTest = !disabled;
      // Groups (the Shells group: surface + edge lines) need each child's material touched.
      for (const child of obj.children) {
        const cmat = (child as THREE.Mesh | THREE.LineSegments).material as
          THREE.Material | THREE.Material[] | undefined;
        if (!cmat) continue;
        for (const m of Array.isArray(cmat) ? cmat : [cmat]) m.depthTest = !disabled;
      }
    }
  }

  public getBounds(): THREE.Box3 {
    return new THREE.Box3().setFromObject(this.root);
  }

  /** Draw a bright overlay over the elements in `payload` (the selected component's
   *  geometry). Pass an empty payload to clear. */
  public setHighlight(payload: FEPayload): void {
    this.clearHighlight();
    const p = payload ?? {};
    const g = new THREE.Group();
    g.name = 'FEHighlight';

    const shells = this.buildSurface(p.shellTris, 0xffb020, 0.85, this.hlDisposables);
    if (shells) { shells.renderOrder = 998; g.add(shells); }
    const solids = this.buildSurface(p.solidTris, 0xffb020, 0.85, this.hlDisposables);
    if (solids) { solids.renderOrder = 998; g.add(solids); }

    // Lines + points drawn always-on-top so the selection reads clearly.
    for (const coords of [p.shellEdges, p.frames, p.links]) {
      const ln = this.buildLines(coords, 0xff7a00, 2, this.hlDisposables);
      if (ln) { ln.renderOrder = 999; (ln.material as THREE.Material).depthTest = false; g.add(ln); }
    }
    const pts = this.buildPoints(p.joints, 0xffd000, 6, this.hlDisposables);
    if (pts) { pts.renderOrder = 1000; (pts.material as THREE.Material).depthTest = false; g.add(pts); }

    if (g.children.length) {
      this.highlight = g;
      this.root.add(g);
    }
  }

  public clearHighlight(): void {
    if (this.highlight) { this.root.remove(this.highlight); this.highlight = null; }
    for (const d of this.hlDisposables) d.dispose();
    this.hlDisposables.length = 0;
  }

  /** A second mesh drawn over this one in a single colour (3D toolbar ▸ Express mesh:
   *  the mesh Bridge Express generated, over the one read back from CSiBridge). Its own
   *  group, outside `root`'s children that setMesh clears, so results re-renders of the
   *  main mesh keep it; it shows and hides with `root`. Edges, frames, links and joints
   *  only, drawn on top — a second set of surfaces would only fight the first. */
  private overlay: THREE.Group | null = null;
  private readonly ovDisposables: Array<THREE.BufferGeometry | THREE.Material> = [];
  public static readonly OVERLAY_COLOR = 0xf08c00;

  public setOverlay(payload: FEPayload): void {
    this.clearOverlay();
    const p = payload ?? {};
    const g = new THREE.Group();
    g.name = 'FEOverlay';
    const c = FEScene.OVERLAY_COLOR;
    for (const [coords, width] of [[p.shellEdges, 1], [p.frames, 2], [p.links, 2]] as Array<[number[] | undefined, number]>) {
      const ln = this.buildLines(coords, c, width, this.ovDisposables);
      if (!ln) continue;
      const m = ln.material as THREE.LineBasicMaterial;
      m.depthTest = false; m.transparent = true; m.opacity = 0.9;
      ln.renderOrder = 996;
      g.add(ln);
    }
    // Solids have no edge list in the payload; a faint surface stands in.
    const solids = this.buildSurface(p.solidTris, c, 0.25, this.ovDisposables);
    if (solids) { solids.renderOrder = 995; g.add(solids); }
    const pts = this.buildPoints(p.joints, c, 4, this.ovDisposables);
    if (pts) { (pts.material as THREE.Material).depthTest = false; pts.renderOrder = 997; g.add(pts); }
    if (!g.children.length) return;
    this.overlay = g;
    this.root.parent?.add(g);
    g.visible = this.root.visible;
  }

  public get hasOverlay(): boolean { return this.overlay != null; }

  /** Follow `root`'s visibility (the host hides the FE view without clearing it). */
  public syncOverlayVisibility(): void {
    if (this.overlay) this.overlay.visible = this.root.visible;
  }

  public getOverlayBounds(): THREE.Box3 | null {
    return this.overlay ? new THREE.Box3().setFromObject(this.overlay) : null;
  }

  public clearOverlay(): void {
    if (this.overlay) { this.overlay.parent?.remove(this.overlay); this.overlay = null; }
    for (const d of this.ovDisposables) d.dispose();
    this.ovDisposables.length = 0;
  }

  public clear(): void {
    this.clearHighlight();
    for (const o of [...this.root.children]) this.root.remove(o);
    this.byType.clear();
    this.shellMesh = null;
    this.shellExtrudeMesh = null;
    this.frameLines = null;
    this.frameMesh = null;
    this.diagramMesh = null;
    this.diagramLines = null;
    this.shellTriElem = [];
    this.shellExtrudeTriElem = [];
    this.frameTriElem = [];
    this.diagramTriElem = [];
    this.jointsObj = null;
    this.shellEdgesObj = null;
    this.linksObj = null;
    this.restraintsObj = null;
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
  }

  // ----- builders -----
  private add(type: string, o: THREE.Object3D): void {
    if (this.hiddenTypes.has(type)) o.visible = false;
    this.byType.set(type, o);
    this.root.add(o);
  }

  private buildSurface(coords: number[] | undefined, color: number, opacity: number,
                       bin: Array<THREE.BufferGeometry | THREE.Material> = this.disposables,
                       colors?: number[]): THREE.Mesh | null {
    if (!coords || coords.length < 9) return null;
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(coords), 3));
    const vertexColors = !!colors && colors.length === coords.length;
    if (vertexColors) geom.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors!), 3));
    geom.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({
      color: vertexColors ? 0xffffff : color, vertexColors,
      transparent: opacity < 1, opacity,
      side: THREE.DoubleSide, flatShading: true,
      metalness: 0.0, roughness: 0.85,
      polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1,
    });
    bin.push(geom, mat);
    const mesh = new THREE.Mesh(geom, mat);
    mesh.name = 'FEShells';
    return mesh;
  }

  /** The frame force diagram's fill: unlit, opaque and outside tone mapping, so each
   *  vertex colour lands on screen exactly as sent — pure red stays pure red, and the
   *  colour-scale fill matches its legend — whatever the side, angle or lighting. */
  private buildDiagram(coords: number[] | undefined, colors: number[] | undefined): THREE.Mesh | null {
    if (!coords || coords.length < 9) return null;
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(coords), 3));
    const vertexColors = !!colors && colors.length === coords.length;
    // The exporter's colours are the ones the legend shows (sRGB); the renderer works in
    // linear and converts back on output, so convert in here to land on the same values.
    // (Through Color: three's typings export SRGBToLinear, its runtime build does not — the
    // bare call type-checked and then threw, which left setMesh half built.)
    if (vertexColors) {
      const lin = new Float32Array(colors!.length);
      const c = new THREE.Color();
      for (let i = 0; i < lin.length; i += 3) {
        c.setRGB(colors![i], colors![i + 1], colors![i + 2], THREE.SRGBColorSpace);   // → linear working space
        lin[i] = c.r; lin[i + 1] = c.g; lin[i + 2] = c.b;
      }
      geom.setAttribute('color', new THREE.BufferAttribute(lin, 3));
    }
    const mat = new THREE.MeshBasicMaterial({
      color: vertexColors ? 0xffffff : 0xb8b8b8, vertexColors,
      side: THREE.DoubleSide, toneMapped: false,
      polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1,
    });
    this.disposables.push(geom, mat);
    const mesh = new THREE.Mesh(geom, mat);
    mesh.name = 'FEDiagram';
    return mesh;
  }

  private buildLines(coords: number[] | undefined, color: number, width: number,
                     bin: Array<THREE.BufferGeometry | THREE.Material> = this.disposables,
                     colors?: number[]): THREE.LineSegments | null {
    if (!coords || coords.length < 6) return null;
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(coords), 3));
    const vertexColors = !!colors && colors.length === coords.length;
    if (vertexColors) geom.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors!), 3));
    const mat = new THREE.LineBasicMaterial({ color: vertexColors ? 0xffffff : color, vertexColors, linewidth: width });
    bin.push(geom, mat);
    return new THREE.LineSegments(geom, mat);
  }

  private buildPoints(coords: number[] | undefined, color: number, size: number,
                      bin: Array<THREE.BufferGeometry | THREE.Material> = this.disposables,
                      colors?: number[]): THREE.Points | null {
    if (!coords || coords.length < 3) return null;
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(coords), 3));
    const vertexColors = !!colors && colors.length === coords.length;
    if (vertexColors) geom.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors!), 3));
    const mat = new THREE.PointsMaterial({ color: vertexColors ? 0xffffff : color, vertexColors, size, sizeAttenuation: false });
    bin.push(geom, mat);
    return new THREE.Points(geom, mat);
  }
}
